// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import type {Review} from 'src/types/crm';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {
  Icon,
  SemanticIcon,
} from '@spaced-out/ui-design-system/lib/components/Icon';
import {useInflight} from 'src/hooks/redux';
import {useHistory} from 'src/rerouter';

import {useApi} from 'src/hooks/useApi';

import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import GetSpecificSections from 'src/components/crm/lib/candidate-evaluation/get-specific-sections.jsx';
import EvaluationFormView from 'src/components/crm/lib/candidate-evaluation/evaluation-form-view.jsx';
import EvaluationFormEdit from 'src/components/crm/lib/candidate-evaluation/evaluation-form-edit.jsx';

// $FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';

import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogFooter,
} from '@spaced-out/ui-design-system/lib/components/Dialog';
import {
  ButtonTextMedium,
  SubTitleSmall,
  SubTitleLarge,
  BodyLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {useSelector, useDispatch} from 'react-redux';
import {selectUserInfo, selectFeedbackConfiguration} from 'src/selectors/crm';
import {
  deleteInterviewReview,
  getFeedbackConfiguration,
} from 'src/action-creators/crm';
//$FlowFixMe[untyped-import]
import {formatMomentDateWithFormat} from 'src/utils/date-time';
import classify from 'src/utils/classify';

import css from './completed-card.css';

// type ReviewType = {|
//   id: number,
//   reviewer: {|
//     id: number,
//     prettyName: string,
//     fullName: string,
//   |},
//   stageName: string,
//   createdOn: string,
//   reviewStatus: string,
//   comment: string,
//   averageFeedbackScore: number,
//   feedbackScores: Array<{|
//     parameterId: string,
//     score: number,
//     remark?: string,
//   |}>,
//   answers: Array<{|
//     questionId: string,
//     answer: string,
//   |}>,
//   jobId: number,
//   stageId: number,
//   stepId: number,
//   stepCandidateId: number,
//   candidateId: number,
//   subject: string,
//   reviewerId: number,
// |};

type Step = {|
  name: string,
|};

type CompletedData = {|
  subject: string,
  candidateName: string,
  jobTitle: string,
  stepName: string,
  stepCandidateId: string,
  candidateId: number,
  jobId: string,
|};

type CompletedCardProps = {|
  completedData: CompletedData,
  isSmall?: boolean,
  type?: string,
  review: Review,
  jobId?: string,
  from?: string,
  onUpdateFeedbacks?: () => void,
|};

export const CompletedCard = ({
  completedData,
  review,
  type,
  isSmall = false,
  onUpdateFeedbacks,
  from,
}: CompletedCardProps): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const history = useHistory();
  const {reviewId} = history.location.query;
  const [viewModal, setViewModal] = React.useState(
    Number(reviewId) === Number(review.id),
  );
  const [editModal, setEditModal] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const SubmittedOn = review.createdOn;
  const Candidate = completedData.candidateName;
  const Overall = review.reviewStatus;
  const AverageScore =
    typeof review.averageFeedbackScore === 'number'
      ? review.averageFeedbackScore.toFixed(1)
      : review.averageFeedbackScore;

  const isFeedbackConfigurationLoading = useInflight(
    getFeedbackConfiguration(),
  );
  const feedbackConfiguration = useSelector(selectFeedbackConfiguration);
  const {user: userInfo} = useSelector(selectUserInfo) || {};

  const {result: stepData, isLoading: isStepDataLoading} = useApi(
    {pathname: `/job-steps/${review.stepId}`},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  const {result: jobScorecard, isLoading: isJobScorecardLoading} = useApi(
    {pathname: `/job-scorecards/${review.jobId}`},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  const {
    result: scorecardStepConfiguration,
    isLoading: isScorecardStepConfigurationLoading,
  } = useApi(
    {pathname: `/step-scorecard-config?stepId=${review.stepId}`},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  const {sections: scorecardJobSections = []} = jobScorecard || {};

  const scorecardStepSections = scorecardJobSections
    .map((section) => {
      const foundStepSection = scorecardStepConfiguration?.find(
        (stepSection) => stepSection.sectionId === section.id,
      );

      return {
        ...section,
        status: foundStepSection ? foundStepSection.status : '',
      };
    })
    .filter((section) => section.status !== 'RESTRICTED');

  React.useEffect(() => {
    if (!feedbackConfiguration) {
      dispatch(getFeedbackConfiguration());
    }
  }, []);

  const cardData = [
    {
      icon: 'calendar',
      text: `${t('Submitted_on', 'Submitted on')}: ${formatMomentDateWithFormat(
        SubmittedOn,
      )}`,
    },
    {
      icon: 'user',
      text: `${t('Candidate', 'Candidate')}: ${Candidate}`,
    },
    {
      icon: 'star',
      text: `${t('Average_Score', 'Average Score')}: ${AverageScore}`,
    },
    {
      icon: 'ranking-star',
      text: `${t('Overall', 'Overall')}: ${Overall}`,
    },
    {
      icon: 'briefcase',
      text: `${t('Job', 'Job')}: ${completedData.jobTitle}`,
    },

    {
      icon: 'compass',
      text: `${t('Step', 'Step')}: ${completedData.stepName}`,
    },
  ];

  const deleteFeedback = () => {
    setIsLoading(true);
    dispatch(deleteInterviewReview(review.id))
      .then(() => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {t(
                'Feedback_deleted_successfully',
                'Feedback deleted successfully',
              )}
            </ToastTitle>
          </Toast>,
        );
        setIsLoading(false);
        onUpdateFeedbacks?.();
      })
      .catch((err) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>
              {t('Error_deleting_feedback', 'Error deleting feedback')}
            </ToastTitle>
          </Toast>,
        );
      });
  };

  const title =
    type === 'applicant' ? `${Candidate}` : `${completedData.subject}`;

  const options = [
    {
      key: '1',
      label: t('Edit', 'Edit'),
    },
    {
      key: '2',
      label: t('Delete', 'Delete'),
    },
  ];
  const handleAction = (action) => {
    if (action === 'Edit') {
      setEditModal(true);
      setViewModal(false);
    } else if (action === 'Delete') {
      setIsDeleteDialogOpen(true);
    }
  };

  const getColor = (iconName) => {
    return ['star', 'ranking-star'].includes(iconName)
      ? 'primary'
      : 'secondary';
  };

  const handleSubmitForm = (evaluation) => {
    const {jobId, stageId, stepId, candidateId} = review;

    const {hideOverallRecommendation, hideScorecard} = stepData || {};

    const newValues = {
      jobId,
      stageId,
      stepId,
      candidateId,
      ...evaluation,
      reviewerId: userInfo.id,
      interviewCandidateId: review.stepCandidateId,
    };

    if (hideScorecard) {
      delete newValues.feedbackScores;
    }
    if (hideOverallRecommendation) {
      delete newValues.reviewStatus;
      delete newValues.comment;
    }

    const apiUrl = review
      ? `api/interview-reviews/${review.id}`
      : 'api/interview-reviews';

    setIsSubmitting(true);

    dispatch(
      reduxApi.post(
        apiUrl,
        {
          ...newValues,
        },
        {},
        {apiPath: '/trmapi/'},
      ),
    )
      .then(() => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {review
                ? `${t(
                    'Feedback_Updated_Successfully',
                    'Feedback Updated Successfully',
                  )}!`
                : `${t(
                    'Feedback_Added_Successfully',
                    'Feedback Added Successfully',
                  )}!`}
            </ToastTitle>
          </Toast>,
        );
        onUpdateFeedbacks?.();
        setEditModal(false);
      })
      .catch((error) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{`${t(
              'Feedback_Update_Failed',
              'Feedback Update Failed',
            )}!`}</ToastTitle>
          </Toast>,
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCloseViewModal = () => {
    setViewModal(false);
    history.push(
      from === 'DASHBOARD'
        ? `/dashboard/feedbacks`
        : `/crm/job/${review.jobId}/info/overview`,
    );
  };

  return (
    <Card classNames={{wrapper: css.card}}>
      <div className={css.cardHeader}>
        <ButtonTextMedium>{title}</ButtonTextMedium>
        <CardActions>
          <Button
            children={t('View_Feedback', 'View Feedback')}
            onClick={() => {
              setViewModal(true);
              // const params = new URLSearchParams(window.location.search);
              // params.set('stepCandidateId', completedData.stepCandidateId);
              // params.set('candidateId', completedData.candidateId.toString());
              // if (from === 'DASHBOARD') {
              //   params.set('jobId', completedData.jobId);
              // }
              // params.set('feedbackTabId', 'RESUME');
              // params.set('type', 'applicant');
              // params.set('isReviewPanelOpen', 'true');
              // params.set('reviewId', review.id.toString());
              // const rerouteUrl =
              //   from === 'DASHBOARD'
              //     ? `/dashboard/feedbacks?${params.toString()}`
              //     : `/crm/job/${
              //         review.jobId
              //       }/info/overview?${params.toString()}`;
              // history.push(rerouteUrl);
            }}
            type="tertiary"
            size="small"
            iconLeftName="file-lines"
          />
          <ButtonDropdown
            iconRightName="ellipsis-vertical"
            menu={{
              isFluid: false,
              options,
              size: 'medium',
            }}
            onOptionSelect={(e) => {
              handleAction(e.label);
            }}
            size="small"
            type="ghost"
          />
        </CardActions>
      </div>
      <CardContent className={css.cardContent}>
        <Card
          classNames={{
            wrapper: css.innerCard,
          }}
          paddingTop="small"
          paddingBottom="small"
        >
          <div
            className={classify(css.feedbackContainerSmall, {
              [css.feedbackContainerLarge]: !isSmall,
            })}
          >
            {cardData.map((item, index) => (
              <div key={index} className={css.feedbackItem}>
                <Icon
                  name={item.icon}
                  size="small"
                  color={getColor(item.icon)}
                />
                <SubTitleSmall color={getColor(item.icon)}>
                  {item.text}
                </SubTitleSmall>
              </div>
            ))}
          </div>
        </Card>
      </CardContent>

      <DeleteReiviewDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        onConfirm={() => {
          deleteFeedback();
          setIsDeleteDialogOpen(false);
        }}
        isLoading={isLoading}
      />
      {viewModal ? (
        <Panel
          isOpen={viewModal}
          onClose={handleCloseViewModal}
          anchor="right"
          size="large"
          hideBackdrop={false}
        >
          <PanelHeader onCloseButtonClick={handleCloseViewModal}>
            {t('View_Feedback', 'View Feedback')}
          </PanelHeader>
          {isJobScorecardLoading ||
          isScorecardStepConfigurationLoading ||
          isFeedbackConfigurationLoading ||
          isStepDataLoading ? (
            <PanelBody className={css.panelBody}>
              <CircularLoader />
            </PanelBody>
          ) : stepData ? (
            <GetSpecificSections
              jobId={review.jobId}
              stageId={review.stageId}
              stepId={review.stepId}
              loaderComponent={
                <PanelBody className={css.panelBody}>
                  <div className={css.loaderContainer}>
                    <CircularLoader />
                  </div>
                </PanelBody>
              }
            >
              {(sections) => (
                <PanelBody className={css.panelBody}>
                  <EvaluationFormView
                    step={stepData}
                    feedbackConfiguration={feedbackConfiguration}
                    scorecardStepSections={scorecardStepSections}
                    subjectiveSections={sections}
                    review={review}
                  />

                  <DeleteReiviewDialog
                    isOpen={isDeleteDialogOpen}
                    setIsOpen={setIsDeleteDialogOpen}
                    onConfirm={() => {
                      deleteFeedback();
                      setIsDeleteDialogOpen(false);
                    }}
                    isLoading={isLoading}
                  />
                </PanelBody>
              )}
            </GetSpecificSections>
          ) : null}
          <PanelFooter classNames={{actions: css.panelFooterActions}}>
            <SemanticIcon
              name="trash"
              onClick={() => {
                setIsDeleteDialogOpen(true);
              }}
              semantic="danger"
              className={css.deleteButton}
            />
            <div className={css.panelFooterActionsRight}>
              <Button isFluid onClick={handleCloseViewModal} type="tertiary">
                {t('Close', 'Close')}
              </Button>
              {review.reviewer && review.reviewer.id === userInfo.id ? (
                <Button
                  isFluid
                  onClick={() => setEditModal(true)}
                  type="primary"
                >
                  {t('Edit', 'Edit')}
                </Button>
              ) : null}
            </div>
          </PanelFooter>
        </Panel>
      ) : null}

      {editModal ? (
        <Panel
          isOpen={editModal}
          onClose={() => {
            setEditModal(false);
            handleCloseViewModal();
          }}
          anchor="right"
          size="large"
          hideBackdrop={false}
        >
          <PanelHeader
            onCloseButtonClick={() => {
              setEditModal(false);
              handleCloseViewModal();
            }}
          >
            {t('Edit_Feedback', 'Edit Feedback')}
          </PanelHeader>
          {isJobScorecardLoading ||
          isScorecardStepConfigurationLoading ||
          isFeedbackConfigurationLoading ||
          isStepDataLoading ? (
            <PanelBody className={css.panelBody}>
              <CircularLoader />
            </PanelBody>
          ) : (
            <PanelBody className={css.panelBodyWithoutPadding}>
              <GetSpecificSections
                jobId={review.jobId}
                stageId={review.stageId}
                stepId={review.stepId}
                loaderComponent={
                  <PanelBody className={css.panelBody}>
                    <div className={css.loaderContainer}>
                      <CircularLoader />
                    </div>
                  </PanelBody>
                }
              >
                {(sections) => (
                  <EvaluationFormEdit
                    step={stepData}
                    feedbackConfiguration={feedbackConfiguration}
                    scorecardStepSections={scorecardStepSections}
                    subjectiveSections={sections}
                    review={review}
                    stepCandidateId={review.stepCandidateId}
                    onSubmit={handleSubmitForm}
                    onCancel={() => setEditModal(false)}
                    alreadyInPanelBody={true}
                    isSubmitting={isSubmitting}
                  />
                )}
              </GetSpecificSections>
            </PanelBody>
          )}
        </Panel>
      ) : null}
    </Card>
  );
};

const DeleteReiviewDialog = ({
  isOpen,
  setIsOpen,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean,
  setIsOpen: (((boolean) => boolean) | boolean) => void,
  onConfirm: () => void,
  isLoading: boolean,
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <Dialog semantic="danger" isOpen={isOpen}>
      <DialogHeader>
        <SubTitleLarge>
          {t('Delete_this_Feedback', 'Delete this Feedback')}
        </SubTitleLarge>
      </DialogHeader>
      <DialogBody>
        <BodyLarge color="secondary">{`${t(
          'Are_you_sure_you_want_to_continue',
          'Are you sure you want to continue',
        )}?`}</BodyLarge>
      </DialogBody>
      <DialogFooter>
        <Button isFluid onClick={() => setIsOpen(false)} type="tertiary">
          {t('No', 'No')}
        </Button>

        <Button isFluid onClick={onConfirm} isLoading={isLoading}>
          {t('Yes_Delete', 'Yes, Delete')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
